import React, { FC, HTMLAttributes, useEffect, useState } from "react";
import { CheckCircleIcon, ClockIcon, ShieldCheckIcon } from "@heroicons/react/24/solid";
import { Helmet } from "react-helmet";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { LocationFormProps } from "data/reduxInterfaces";

const formSchema = z.object({
  isChecked1: z.boolean().refine((val) => val === true, {
    message: "Vous devez accepter les conditions générales d'utilisation",
  }),
  isChecked2: z.boolean().refine((val) => val === true, {
    message: "Vous devez confirmer avoir les droits et autorités nécessaires",
  }),
});

const LocationConfirmationModern: FC<LocationFormProps & HTMLAttributes<HTMLDivElement>> = ({
  className,
  isLoading,
  handleForm,
  errorLocationForm,
  FormData,
}) => {
  const [checkboxes, setCheckboxes] = useState({
    isChecked1: false,
    isChecked2: false,
  });

  const handleCheckbox1Change = () => {
    const newValue = !checkboxes.isChecked1;
    setCheckboxes({ ...checkboxes, isChecked1: newValue });
    setValue("isChecked1", newValue);
  };

  const handleCheckbox2Change = () => {
    const newValue = !checkboxes.isChecked2;
    setCheckboxes({ ...checkboxes, isChecked2: newValue });
    setValue("isChecked2", newValue);
  };

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {},
    mode: "onSubmit",
  });

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    FormData(data);
  };

  useEffect(() => {
    if (handleForm.type === "confirmLocation") {
      handleSubmit(onSubmit)();
    }
  }, [handleForm]);

  return (
    <div className={`${className}`}>
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>

      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
            <CheckCircleIcon className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Félicitations !</h2>
        <p className="text-gray-600 dark:text-gray-400">Votre espace est prêt à être examiné par notre équipe</p>
        <div className="border-b-2 w-20 border-eventToBee mt-4 mx-auto"></div>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        {/* Section de confirmation */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center mb-6">
            <ShieldCheckIcon className="w-6 h-6 text-eventToBee mr-3" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Confirmation et acceptation</h3>
          </div>

          <div className="space-y-4">
            {/* Checkbox 1 - CGU */}
            <div
              className={`p-4 rounded-lg border-2 transition-colors ${
                checkboxes.isChecked1
                  ? "border-green-200 bg-green-50 dark:bg-green-900/10 dark:border-green-800"
                  : "border-gray-200 bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
              }`}
            >
              <label className="flex items-start space-x-3 cursor-pointer">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    checked={checkboxes.isChecked1}
                    onChange={handleCheckbox1Change}
                    className="w-4 h-4 text-eventToBee bg-gray-100 border-gray-300 rounded focus:ring-eventToBee dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div className="text-sm">
                  <p className="text-gray-900 dark:text-white font-medium">
                    J'accepte les conditions générales d'utilisation
                  </p>
                  <p className="text-gray-600 dark:text-gray-400 mt-1">
                    En cochant cette case, vous acceptez nos{" "}
                    <a href="#" className="text-eventToBee hover:underline">
                      conditions générales d'utilisation
                    </a>{" "}
                    et notre politique de confidentialité.
                  </p>
                </div>
              </label>
              {errors.isChecked1?.message && (
                <div className="mt-2 text-xs text-red-600 dark:text-red-400">{errors.isChecked1.message}</div>
              )}
            </div>

            {/* Checkbox 2 - Droits */}
            <div
              className={`p-4 rounded-lg border-2 transition-colors ${
                checkboxes.isChecked2
                  ? "border-green-200 bg-green-50 dark:bg-green-900/10 dark:border-green-800"
                  : "border-gray-200 bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
              }`}
            >
              <label className="flex items-start space-x-3 cursor-pointer">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    checked={checkboxes.isChecked2}
                    onChange={handleCheckbox2Change}
                    className="w-4 h-4 text-eventToBee bg-gray-100 border-gray-300 rounded focus:ring-eventToBee dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div className="text-sm">
                  <p className="text-gray-900 dark:text-white font-medium">Confirmation des droits et autorités</p>
                  <p className="text-gray-600 dark:text-gray-400 mt-1">
                    Je confirme que j'ai le droit, le pouvoir et l'autorité nécessaires pour proposer l'espace à
                    louer sur le site, ainsi que pour collecter des paiements dans le cadre de la location de cet
                    espace.
                  </p>
                </div>
              </label>
              {errors.isChecked2?.message && (
                <div className="mt-2 text-xs text-red-600 dark:text-red-400">{errors.isChecked2.message}</div>
              )}
            </div>
          </div>

          {/* Statut de validation */}
          {checkboxes.isChecked1 && checkboxes.isChecked2 && (
            <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center space-x-2">
                <CheckCircleIcon className="w-5 h-5 text-green-500" />
                <span className="text-sm font-medium text-green-700 dark:text-green-300">
                  Toutes les conditions sont acceptées
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Section d'information sur le processus */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-6">
          <div className="flex items-center mb-4">
            <ClockIcon className="w-6 h-6 text-blue-500 mr-3" />
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200">Processus d'approbation</h3>
          </div>

          <div className="space-y-3 text-sm text-blue-800 dark:text-blue-200">
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p>
                Une fois que notre équipe aura approuvé votre demande, elle sera publiée en ligne sur{" "}
                <a className="font-semibold text-eventToBee hover:underline" href="http://eventtobee.com">
                  eventtobee.com
                </a>
              </p>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p>
                <span className="font-medium">Délai habituel :</span> Le processus d'approbation prend généralement{" "}
                <span className="font-semibold">72 heures ouvrables</span>
              </p>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p>
                <span className="font-medium">Forte demande :</span> En raison d'une forte demande actuelle, il se
                peut qu'il y ait une liste d'attente pour l'approbation
              </p>
            </div>
          </div>

          <div className="mt-4 p-3 bg-white dark:bg-blue-800 rounded-lg border border-blue-300 dark:border-blue-700">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                📧 Vous recevrez une notification par e-mail dès que votre espace sera approuvé !
              </span>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LocationConfirmationModern;
